import { CommonModule, CurrencyPipe } from "@angular/common";
import { Component, EventEmitter, inject, Input, LOCALE_ID, OnChanges, Output } from "@angular/core";

import { UiKitModule } from "@app/ui-kit.module";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { ProductListDiscountKey } from "@domain/project/product-list/product-list-discount-key.util";
import {
  calculateDiscountedTotal,
  formatProductListDiscount,
  getProductListPriceData,
  isProductListDiscountEditable,
  normalizeProductListDiscountDisplay,
} from "@domain/project/product-list/product-list-discount.util";
import { ProductListService } from "@domain/project/product-list/product-list.service";
import { ProductSection } from "@domain/project/product-list/product-section";

@Component({
  selector: "app-product-table",
  templateUrl: "./product-table.component.html",
  styleUrls: ["./product-table.component.scss"],
  imports: [CurrencyPipe, CommonModule, UiKitModule],
  standalone: true,
})
export class ProductTableComponent implements OnChanges {
  private productListService = inject(ProductListService);
  private readonly locale = inject(LOCALE_ID);

  @Input() showCosts!: boolean;
  @Input() showZrefPrices = false;
  @Input() canEditDiscounts = false;
  @Input() productSection!: ProductSection<
    FloorplanAlarmDevice | FloorplanGasWarningCenter | FloorplanPlasticSign | FloorplanSignalElement | FloorplanTransmitter,
    FloorplanPlaceholder,
    | AlarmDeviceConfiguration
    | GasWarningCenterConfiguration
    | PlasticSignConfiguration
    | SignalElementConfiguration
    | TransmitterConfiguration,
    ConfigProductMap
  >;
  protected placeholderWithProductsCount = 1;
  protected placeholderWithoutProductsCount = 0;

  @Output() discountChange = new EventEmitter<{ key: ProductListDiscountKey; value: string }>();

  ngOnChanges(): void {
    this.placeholderWithoutProductsCount = this.countPlaceholderWithoutId();
  }

  protected readonly tooltipTextWarningSign: string = $localize`:@@productlist.table.warningSign.tooltip:Produkt enthält abgekündigte Komponenten oder ist Teil einer unvollständigen Konfiguration`;

  protected countPlaceholderWithoutId() {
    return this.productListService.countUnspecifiedPlaceholders(this.productSection.placeholders);
  }

  protected hasEditablePrice(product: ProductData | PlaceholderProduct): boolean {
    return isProductListDiscountEditable(
      this.canEditDiscounts,
      this.showCosts,
      getProductListPriceData(product, this.showZrefPrices),
      !!product.discountKey,
    );
  }

  protected hasPrice(product: ProductData | PlaceholderProduct): boolean {
    return !!getProductListPriceData(product, this.showZrefPrices);
  }

  protected getUnitPrice(product: ProductData | PlaceholderProduct): number | undefined {
    return getProductListPriceData(product, this.showZrefPrices)?.price;
  }

  protected getTotalPrice(product: ProductData | PlaceholderProduct, placedProductsCount: number): number | undefined {
    const unitPrice = this.getUnitPrice(product);
    return unitPrice === undefined
      ? undefined
      : calculateDiscountedTotal(unitPrice, placedProductsCount * this.getProductQuantity(product), product.discount ?? 0);
  }

  protected formatDiscount(discount: number): string {
    return formatProductListDiscount(discount, this.locale);
  }

  protected normalizeDiscountInput(input: HTMLInputElement, product: ProductData | PlaceholderProduct): void {
    const normalizedDiscount = normalizeProductListDiscountDisplay(input.value, product.discount ?? 0, this.locale);
    input.value = normalizedDiscount.formattedValue;
    if (normalizedDiscount.discount !== undefined && product.discountKey) {
      this.discountChange.emit({ key: product.discountKey, value: String(normalizedDiscount.discount) });
    }
  }

  private getProductQuantity(product: ProductData | PlaceholderProduct): number {
    return product instanceof PlaceholderProduct ? 1 : product.quantity;
  }

  protected productBelongsToConfig(
    product:
      FloorplanGasWarningCenter | FloorplanAlarmDevice | FloorplanSignalElement | FloorplanPlasticSign | FloorplanTransmitter,
    mapping: ConfigProductMap,
  ) {
    return product.config.id === mapping.config.id;
  }
}
